import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { EventsTabContent } from './events-tab-content';
import {
  useWorkflowPanelStore,
  WorkflowPanelTab,
} from '@/stores/workflow-panel-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { NodeStatusEvent, WorkflowEventTypes } from '@/types/workflow-types';
import {
  GripVerticalIcon,
  X,
  PencilLine,
  ChevronDown,
  Settings2,
  Plus,
  CircleFadingArrowUp,
  CornerLeftUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { NodeInputRenderer } from '../custom-node/generic-node/node-input-renderer';
// --- repeat loop (Phase 3) ---------------------------------------------------
import { RepeatUntilPanel } from '../custom-node/generic-node/repeat-until-panel';
import { RegionNodeRunCard } from '../custom-node/generic-node/repeat-region-chip';
import { ForEachRegionCard } from '../custom-node/generic-node/for-each-region-chip';
import {
  CollectFoldCard,
  REDUCE_NODE_ID,
  UNION_NODE_ID,
} from '../custom-node/generic-node/collect-fold-card';
import { REPEAT_NODE_ID } from '@/pages/workflow/helper/repeat-control';
import { plainNodeDescription } from '@/pages/workflow/helper/catalog-copy';
// --- Phase 6: the Map / For Each node is configured as a form ---
import { MAP_NODE_ID } from '@/pages/workflow/helper/map-control';
import { MapPanel } from '../custom-node/generic-node/map-panel';
// --- end Phase 6 ---
// --- end repeat loop ---------------------------------------------------------
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { CustomNode, NodeParam, NodeType } from '@/types/node-types';
import { isCanvasDecorationNode } from '@/pages/workflow/helper/graph-analysis';
import { getAbsolutePosition } from '@/pages/workflow/helper/group-nodes';
import { cn } from '@/lib/utils';
import {
  CONSOLE_PANEL_MIN_WIDTH,
  CONSOLE_PANEL_MAX_WIDTH,
} from '@/constants/workflow-editor';
import { Separator } from '@/components/ui/separator';
import { WorkflowNodeList } from './workflow-node-list';
import { IconTile } from '../icon-tile';
import { getNodeIconColor } from '@/constants/node-styling';
import {
  useReactFlow,
  useViewport,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { useResizablePanel } from '@/hooks/use-resizable-panel';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { FilesTabContent } from './files-tab-content';
import { EvalsTabContent } from './evals-tab-content';
import { ErrorsTabContent } from './errors-tab-content';
import { ScheduleConfigSection } from './schedule-sidebar';
import { NodeLaunchesSection } from './node-launches-section';
import { DELEGABLE_WORKFLOWS_FIELD_ID } from '@/pages/workflow/component/custom-node/generic-node/agent-capability-chips';
import { TriggersConfigSection } from './triggers-sidebar';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { useOrgStore } from '@/stores/org-store';
import { LabelPicker, LabelBadges } from '@/components/label-picker';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';
import { useNotification } from '@/components/notifications';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { RightPanelSlot, rightPanelCardClasses } from './right-panel-slot';
import { OverflowTabsList, type OverflowTabItem } from './overflow-tabs-list';
import { McpPublishTabContent } from './mcp-publish-tab';
import { isSourceViewAvailable } from './source-code-panel';
import { SourceCodeWorkspace } from './source-code-editor';
import { CustomCodeScriptCard } from './custom-code-script-card';
// --- explain every decision (Phase 4) ---
import { DecisionWhyCard } from '../custom-node/generic-node/decision-why-card';
// --- end explain ---
import { CUSTOM_CODE_NODE_ID } from '@/pages/workflow/helper/custom-code-manifest';
interface ConsoleSidebarProps {
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  isConnected: boolean;
  headerButtons: React.ReactNode;
  panelGroupWidth?: number;
  onEditWorkflowInfo?: () => void;
  isLocked?: boolean;
  isWorkflowLocked?: boolean;
  /** Persisted workflow id for Evals tab API calls. */
  workflowId?: string | null;
  /**
   * Workflow run scope for eval results. Must be the agentic-backend
   * `session_id` (which is what the eval node forwards to platform-api as
   * `workflow_run_id`), NOT the platform `WorkflowRun.id`. Omit when no run
   * is selected — the Evals tab will then prompt the user to pick one.
   */
  workflowRunId?: string | null;
  /**
   * Source run's session_id when the current run is a rerun-from-node.
   * The Evals tab fetches results from this run as a fallback for any
   * eval node that was cached/skipped in the current run (since the
   * backend doesn't duplicate OpenSearch eval docs into the new
   * session for cached nodes). Null for fresh runs and pure past-run
   * views.
   */
  parentRunId?: string | null;
  /**
   * react_flow_generated_ids of nodes seeded as cached in the current
   * rerun-from-node run. Used by the Evals tab to know which eval
   * nodes to merge from `parentRunId` instead of expecting fresh
   * results in `workflowRunId`.
   */
  cachedEvalNodeIds?: Set<string>;
  /**
   * True while the workflow is actively executing on this canvas.
   * The Evals tab uses this to keep auto-polling indefinitely during a
   * live run (results trickle in as eval nodes finish) and only cap the
   * polling at 3 attempts once the workflow has terminated — that's
   * when a missing result indicates a real backend stall, not just
   * "still running".
   */
  isWorkflowExecuting?: boolean;
  isWorkflowPaused?: boolean;
  /**
   * Platform `WorkflowRun.id` of the currently viewed past run, if any.
   * The Source tab uses it to offer the immutable run-snapshot view of a
   * pinned custom-code node's source; without it the tab still opens the
   * editable current-pin view.
   */
  workflowRunRecordId?: string | null;
}

const WORKFLOW_LOCK_EDITABLE_FIELDS = new Set([
  'task',
  'user_prompt',
  'request',
]);

const SHOW_ADVANCED_SETTINGS = false;

const WORKFLOW_CARD_LINK_LABELS: Record<WorkflowPanelTab, string> = {
  configure: 'Workflow config',
  events: 'Workflow events',
  files: 'Workflow artifacts',
  evals: 'Workflow evals',
  mcp: 'Workflow MCP',
  api: 'Workflow API',
  errors: 'Workflow errors',
  // Source is node-scoped; leaving node scope lands on the config tab.
  source: 'Workflow config',
};

export const tabTriggerClasses =
  'flex-initial shrink-0 h-7 px-3 py-1 rounded-md text-sm font-medium text-muted-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:border-transparent';

export const ConsoleSidebar: React.FC<ConsoleSidebarProps> = ({
  nodeStatuses,
  isConnected,
  headerButtons,
  panelGroupWidth: _panelGroupWidth = 1400,
  onEditWorkflowInfo,
  isLocked = false,
  isWorkflowLocked = false,
  workflowId = null,
  workflowRunId = null,
  parentRunId = null,
  cachedEvalNodeIds,
  isWorkflowExecuting = false,
  isWorkflowPaused = false,
  workflowRunRecordId = null,
}) => {
  const {
    activeTab,
    setActiveTab,
    mcpCode,
    mcpEndpoint,
    hasMcpTabBeenViewed,
    setFilteredNodeId,
    isMaximized,
    setMaximized,
    isConsolePanelOpen,
    isWorkflowCardOpen,
    setConsolePanelOpen,
    openWorkflowCard,
    markPublishTabViewed,
    panelSize,
    setPanelSize,
    highlightedFieldId,
    highlightedNodeId,
    setHighlightedField,
    nodePanelData,
    cardTabs,
    setCardTab,
  } = useWorkflowPanelStore();

  // Track which nodes have their advanced settings expanded (collapsed by default)
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState<
    Record<string, boolean>
  >({});

  // Handle expandAdvancedSettings from nodePanelData (triggered by FloatingControlBar's Advanced button)
  useEffect(() => {
    if (nodePanelData?.expandAdvancedSettings && nodePanelData?.nodeId) {
      setAdvancedSettingsOpen((prev) => ({
        ...prev,
        [nodePanelData.nodeId!]: true,
      }));
    }
  }, [nodePanelData]);

  // Calculate notification status internally
  const showNotification =
    (isConnected &&
      Object.values(nodeStatuses).some((statuses) =>
        statuses.some(
          (status) => status.event_type === WorkflowEventTypes.STATUS_UPDATE,
        ),
      )) ||
    (!!mcpCode && !hasMcpTabBeenViewed);

  const { isLoadingWorkflow, currentWorkflow } = useWorkflowStore();
  const { currentOrg } = useOrgStore();
  const { isLabelsEnabled, isWorkflowSchedulingEnabled, isTriggersEnabled } =
    useLaunchDarkly();
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const {
    nodes,
    edges,
    groupedNodes,
    lastSelectedNodeId,
    selectNode: storeSelectNode,
    deselectAllNodes,
    deselectNode,
    handleParameterChange: storeHandleParameterChange,
    updateNodeToCurrentVersion,
  } = useNodeStore();
  const updateAvailableNodes = useNodeStore((s) => s.updateAvailableNodes);

  const { setCenter } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const { zoom } = useViewport();

  const {
    unsupportedNodes,
    missingFiles,
    fieldErrors,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
  } = useWorkflowValidationStore();
  const notification = useNotification();

  // Show selected node in Configure tab
  const nodeIdToNodeMap = useMemo(() => {
    const nodeIdToNodeArray = selectedNodeIds
      .map((nodeId) => {
        return [nodeId, nodes.find((node) => node.id === nodeId)] as const;
      })
      .filter(
        (entry): entry is readonly [string, CustomNode] =>
          entry[1] !== undefined &&
          entry[1].data?.struct !== undefined &&
          !isCanvasDecorationNode(entry[1]),
      );
    return Object.fromEntries(nodeIdToNodeArray) as Record<string, CustomNode>;
  }, [selectedNodeIds, nodes]);

  // Check if we have any nodes to configure (either hovered or selected)

  const describeNode = useCallback((nodeId: string, node: CustomNode) => {
    const struct = node.data.struct;
    const iconColor = getNodeIconColor(struct.node_type, struct.icon_color);
    return {
      nodeId,
      name: struct.user_config_name || struct.display_name,
      // --- Phase 6: catalog text for people (no implementation jargon) ---
      description: plainNodeDescription(struct),
      // --- end Phase 6 ---
      icon: struct.icon,
      iconColor,
      useThemeColors: iconColor === null,
      nodeSubType: struct.node_sub_type,
      // Catalog id (e.g. 'custom_code_node') — gates the Source tab.
      catalogNodeId: struct.node_id,
    };
  }, []);

  const isNodeUpdatable = useCallback(
    (node: ReturnType<typeof describeNode>) =>
      updateAvailableNodes.some((n) => n.nodeId === node.nodeId) &&
      !isLocked &&
      (!isWorkflowLocked ||
        node.nodeSubType === 'file_input' ||
        node.nodeSubType === 'text_input'),
    [updateAvailableNodes, isLocked, isWorkflowLocked],
  );

  const handleUpdateNode = useCallback(
    (nodeId: string) => {
      updateNodeToCurrentVersion(nodeId, groupedNodes);
      // Defer by one tick so the store update commits before ReactFlow
      // re-measures handle positions and edge anchors; otherwise edges
      // visually drift to stale coordinates.
      setTimeout(() => updateNodeInternals(nodeId), 0);
    },
    [groupedNodes, updateNodeToCurrentVersion, updateNodeInternals],
  );

  // Full id → node lookup used by Phase 2 eval filtering (O(1) vs O(n) find)
  const nodesById = useMemo(
    () => new Map(nodes.map((n) => [n.id, n])),
    [nodes],
  );

  const currentWorkflowLabels = currentWorkflow?.labels ?? [];

  // Get all workflow nodes (excluding canvas-only / non-configurable nodes)
  const workflowNodes = useMemo(() => {
    return nodes.filter(
      (node) =>
        node.type !== NodeType.SelectNode &&
        node.type !== NodeType.StickyNoteNode &&
        node.type !== NodeType.ArtifactNode &&
        node.type !== NodeType.GroupNode &&
        node.data?.struct !== undefined,
    );
  }, [nodes]);

  // All eval nodes on the canvas — powers the per-node filter in the Evals tab.
  // Also resolves each Evals node's upstream node display names from the
  // workflow edge graph (edge.target === this evals-node id) so the CSV
  // export can surface "which upstream node is being scored" alongside
  // the Evals node's own name.
  const allEvalNodes = useMemo(() => {
    const nameForNode = (id: string): string | null => {
      const n = nodesById.get(id);
      if (!n) return null;
      return (
        n.data?.struct?.user_config_name || n.data?.struct?.display_name || null
      );
    };

    // edge.target -> Set of edge.source node ids. Built once so each
    // eval node lookup is O(in-degree) rather than O(edges).
    const upstreamByTarget = new Map<string, Set<string>>();
    for (const e of edges) {
      if (!e?.target || !e?.source) continue;
      let bucket = upstreamByTarget.get(e.target);
      if (!bucket) {
        bucket = new Set();
        upstreamByTarget.set(e.target, bucket);
      }
      bucket.add(e.source);
    }

    return nodes
      .filter((n) => n.data?.struct?.node_sub_type === 'evals')
      .map((n) => {
        const params = n.data!.struct!.params ?? [];
        const multiSelectParam = params.find(
          (p: NodeParam) =>
            p.display_modal === 'MultiSelect' && Array.isArray(p.value),
        );
        const upstreamIds = upstreamByTarget.get(n.id) ?? new Set<string>();
        const upstreamNodeNames = Array.from(upstreamIds)
          .map(nameForNode)
          .filter((s): s is string => !!s);
        return {
          nodeId: n.id,
          name:
            n.data!.struct!.user_config_name ||
            n.data!.struct!.display_name ||
            'Evaluations',
          evalCount: Array.isArray(multiSelectParam?.value)
            ? (multiSelectParam!.value as unknown[]).length
            : 0,
          upstreamNodeNames,
        };
      });
  }, [nodes, edges, nodesById]);

  // When a single eval node (or a node connected to exactly one eval node)
  // is selected on the canvas, auto-select that node in the filter dropdown.
  const autoSelectedEvalNodeId = useMemo(() => {
    for (const id of selectedNodeIds) {
      const struct = nodeIdToNodeMap[id]?.data?.struct;
      if (!struct) continue;

      if (struct.node_sub_type === 'evals') return id;

      const connectedEvalNodes = edges
        .filter((e) => e.source === id || e.target === id)
        .map((e) => nodesById.get(e.source === id ? e.target : e.source))
        .filter(
          (n): n is CustomNode => n?.data?.struct?.node_sub_type === 'evals',
        );

      if (connectedEvalNodes.length === 1) return connectedEvalNodes[0].id;
    }
    return null;
  }, [selectedNodeIds, nodeIdToNodeMap, edges, nodesById]);

  // Determine which tabs should be shown based on available code
  const showScheduleTab =
    isWorkflowSchedulingEnabled && !!workflowId && workflowId !== 'new';
  // Same 'new' guard: a trigger needs a persisted workflow to bind to.
  const showTriggersSection =
    isTriggersEnabled && !!workflowId && workflowId !== 'new';

  const availableTabs = useMemo(() => {
    const tabs = ['configure', 'events', 'files', 'evals'];
    if (mcpCode) {
      tabs.push('mcp');
    }
    if (
      unsupportedNodes.length > 0 ||
      missingFiles.length > 0 ||
      fieldErrors.length > 0 ||
      nodeListUnavailable ||
      multipleWorkflows !== null ||
      cyclicGraph !== null
    ) {
      tabs.push('errors');
    }
    return tabs;
  }, [
    mcpCode,
    unsupportedNodes.length,
    missingFiles.length,
    fieldErrors.length,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
  ]);

  // Check if the MCP tab is newly available (has a notification)
  const showMcpNotification = mcpCode && !hasMcpTabBeenViewed;
  const showErrorsNotification =
    unsupportedNodes.length > 0 ||
    missingFiles.length > 0 ||
    fieldErrors.length > 0 ||
    nodeListUnavailable ||
    multipleWorkflows !== null ||
    cyclicGraph !== null;

  // Reusable component for code tabs
  const CodeTabContent = ({
    value,
    code,
    language = 'python',
  }: {
    value: string;
    code: string;
    language?: string;
  }) => (
    <TabsContent
      value={value}
      className='flex-1 flex flex-col mt-0 min-h-0 p-2 h-0 relative'
    >
      <CopyButton text={code} size='default' />
      <div className='flex-1 overflow-y-auto bg-zinc-100 dark:bg-zinc-800 p-4 rounded-md h-0 relative'>
        <SyntaxHighlighter code={code} language={language} />
      </div>
    </TabsContent>
  );

  // Handle active tab validation - switch to events if current tab is no longer available
  // Don't reset during workflow loading to avoid race conditions with URL params
  useEffect(() => {
    if (!isLoadingWorkflow && !availableTabs.includes(activeTab)) {
      setActiveTab('events');
    }
  }, [activeTab, availableTabs, setActiveTab, isLoadingWorkflow]);

  // Auto-hide errors tab when all errors are resolved
  const prevErrorCountRef = useRef<number>(0);
  useEffect(() => {
    const currentErrorCount =
      unsupportedNodes.length +
      missingFiles.length +
      fieldErrors.length +
      (nodeListUnavailable ? 1 : 0) +
      (multipleWorkflows !== null ? 1 : 0) +
      (cyclicGraph !== null ? 1 : 0);
    const previousErrorCount = prevErrorCountRef.current;

    if (
      previousErrorCount > 0 &&
      currentErrorCount === 0 &&
      activeTab === 'errors'
    ) {
      notification.success('All workflow issues have been resolved!');

      setActiveTab('configure');
    }

    prevErrorCountRef.current = currentErrorCount;
  }, [
    unsupportedNodes.length,
    missingFiles.length,
    fieldErrors.length,
    activeTab,
    setActiveTab,
    notification,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
  ]);

  const focusedNodeId = selectedNodeIds[0] || null;

  // Resize handling via shared hook
  const { handleResizeStart, panelWidth } = useResizablePanel({
    panelSize,
    setPanelSize,
    edge: 'left', // Console sidebar has resize handle on left edge
    minSize: CONSOLE_PANEL_MIN_WIDTH,
    maxSize: CONSOLE_PANEL_MAX_WIDTH,
  });

  // Auto-filter Events tab by the focused node
  useEffect(() => {
    if (focusedNodeId) {
      setFilteredNodeId(focusedNodeId);
    } else {
      // Clear filter when no node is focused
      setFilteredNodeId(null);
    }
  }, [focusedNodeId, setFilteredNodeId]);

  // Calculate sidebar visibility state:
  // - Maximized: always show pinned panel
  // - Not maximized + node focused: show floating panel
  // - Not maximized + no node focused: show minimized button
  const showFloatingPanel = !isMaximized && isConsolePanelOpen;
  const showMinimized = !isMaximized && !isConsolePanelOpen;

  const handleNodeSelect = useCallback(
    (nodeId: string) => {
      // Find the node to get its position
      const node = workflowNodes.find((n) => n.id === nodeId);

      if (node) {
        // Grouped children store position relative to their parent group,
        // not absolute canvas coordinates — resolve the absolute position
        // so centering doesn't pan to the wrong spot.
        const absolutePosition = getAbsolutePosition(node, nodesById);
        setCenter(absolutePosition.x + 100, absolutePosition.y + 50, {
          zoom,
          duration: 500,
        });
      }

      storeSelectNode(nodeId);
    },
    [workflowNodes, nodesById, setCenter, zoom, storeSelectNode],
  );

  // Handler to close panel and deselect all nodes
  const handleClosePanel = useCallback(() => {
    deselectAllNodes();
    setMaximized(false);
  }, [deselectAllNodes, setMaximized]);

  const cardNodeIds = useMemo(
    () => Object.keys(nodeIdToNodeMap),
    [nodeIdToNodeMap],
  );
  const cardNodeCount = cardNodeIds.length;

  // True when this node card can show the Source tab (pinned deterministic
  // custom-code nodes on a persisted workflow).
  const nodeHasSourceTab = useCallback(
    (nodeId: string) =>
      isSourceViewAvailable({
        isSingleNode: true,
        nodeType: nodeIdToNodeMap[nodeId]?.data?.struct?.node_id,
        orgId: currentOrg?.orgid,
        workflowId,
      }),
    [nodeIdToNodeMap, currentOrg?.orgid, workflowId],
  );

  // A card can be left on a tab that has since gone away (the workflow's
  // errors were fixed, say), which would render a blank.
  const resolveCardTab = useCallback(
    (nodeId: string) => {
      const cardTab = cardTabs[nodeId];
      if (!cardTab) return activeTab;
      // Source is node-scoped and never in the workflow-level tab list.
      if (cardTab === 'source') {
        return nodeHasSourceTab(nodeId) ? cardTab : activeTab;
      }
      return availableTabs.includes(cardTab) ? cardTab : activeTab;
    },
    [cardTabs, availableTabs, activeTab, nodeHasSourceTab],
  );

  // Leaving node scope lands on the workflow's own card on the tab the node
  // card was showing, so a user reading one node's events ends up on the
  // whole run's. Only an explicit close closes the panel.
  const handleShowWorkflowCard = useCallback(
    (tab: WorkflowPanelTab) => {
      deselectAllNodes();
      // Source has no workflow-level equivalent — land on config instead.
      openWorkflowCard(tab === 'source' ? 'configure' : tab);
    },
    [deselectAllNodes, openWorkflowCard],
  );

  // The tab of the last-selected node card while the panel is open for nodes;
  // null once the panel is closed or showing the workflow card on purpose.
  const nodeCardTabRef = useRef<WorkflowPanelTab | null>(null);

  useEffect(() => {
    if (!isConsolePanelOpen || isWorkflowCardOpen) {
      nodeCardTabRef.current = null;
      return;
    }
    if (cardNodeCount > 0) {
      const lastCardId =
        lastSelectedNodeId && nodeIdToNodeMap[lastSelectedNodeId]
          ? lastSelectedNodeId
          : cardNodeIds[cardNodeCount - 1];
      nodeCardTabRef.current = resolveCardTab(lastCardId);
    }
  }, [
    isConsolePanelOpen,
    isWorkflowCardOpen,
    cardNodeCount,
    cardNodeIds,
    nodeIdToNodeMap,
    lastSelectedNodeId,
    resolveCardTab,
  ]);

  // The last card going away — deselected by a canvas click or deleted —
  // exits node scope the same way, rather than closing the panel.

  useEffect(() => {
    const lastTab = nodeCardTabRef.current;
    if (
      isConsolePanelOpen &&
      !isWorkflowCardOpen &&
      lastTab !== null &&
      cardNodeCount === 0
    ) {
      nodeCardTabRef.current = null;
      openWorkflowCard(lastTab === 'source' ? 'configure' : lastTab);
    }
  }, [cardNodeCount, isConsolePanelOpen, isWorkflowCardOpen, openWorkflowCard]);

  const handleCardTabChange = useCallback(
    (nodeId: string, tab: WorkflowPanelTab) => {
      setCardTab(nodeId, tab);
      markPublishTabViewed(tab);
    },
    [setCardTab, markPublishTabViewed],
  );

  // Closing a card drops only that node; closing the last one closes the panel.
  const handleCloseNodeCard = useCallback(
    (nodeId: string) => {
      // Sticky notes and other decorations are selectable but never get a
      // card, so the count that matters is the cards, not the selection.
      const remaining = Object.keys(nodeIdToNodeMap).filter(
        (id) => id !== nodeId,
      );
      deselectNode(nodeId);
      if (remaining.length === 0) {
        setConsolePanelOpen(false);
        setMaximized(false);
      }
    },
    [deselectNode, nodeIdToNodeMap, setConsolePanelOpen, setMaximized],
  );

  // Panel closed: the action cluster alone floats over the canvas. The panel
  // is reached by selecting a node, so the row carries no opener of its own.
  if (showMinimized) {
    return (
      <div className='absolute top-2 right-2 z-50 flex items-center gap-2'>
        {showNotification && (
          <span className='size-3 animate-pulse rounded-full bg-red-500' />
        )}
        {headerButtons}
      </div>
    );
  }

  // The card owns its own close; the action cluster above it acts on the
  // workflow, not on this panel. Pinned to the edge, the panel header carries
  // its own close instead.
  // One card's contents. `scopedNode` is the node this card speaks for; null
  // means the card is the workflow's own.
  const errorCount =
    unsupportedNodes.length +
    missingFiles.length +
    fieldErrors.length +
    (nodeListUnavailable ? 1 : 0) +
    (multipleWorkflows !== null ? 1 : 0) +
    (cyclicGraph !== null ? 1 : 0);

  const notificationDot = (
    <span className='absolute -top-1 -left-1 size-3 animate-pulse rounded-full bg-red-500' />
  );

  const menuNotificationDot = (
    <span className='size-2 animate-pulse rounded-full bg-red-500' />
  );

  const tabItems: OverflowTabItem[] = [
    { value: 'configure', label: 'Configure' },
    { value: 'events', label: 'Events' },
    { value: 'files', label: 'Artifacts' },
    { value: 'evals', label: 'Evals' },
    ...(mcpCode
      ? [
          {
            value: 'mcp',
            label: 'MCP',
            badge: showMcpNotification ? notificationDot : undefined,
            menuBadge: showMcpNotification ? menuNotificationDot : undefined,
          },
        ]
      : []),
    ...(showErrorsNotification
      ? [
          {
            value: 'errors',
            label: 'Errors',
            badge: (
              <span className='absolute -top-1 -right-1 rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground'>
                {errorCount}
              </span>
            ),
            menuBadge: (
              <span className='rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground'>
                {errorCount}
              </span>
            ),
          },
        ]
      : []),
  ];

  const renderPanelBody = (
    scopedNode: ReturnType<typeof describeNode> | null,
    tabValue: WorkflowPanelTab,
    onTabChange: (tab: WorkflowPanelTab) => void,
    onCardClose: () => void,
  ) => {
    // Pinned custom-code node cards get a Source tab right after Configure.
    const showSourceTab =
      scopedNode !== null && nodeHasSourceTab(scopedNode.nodeId);
    const cardTabItems: OverflowTabItem[] = showSourceTab
      ? [
          tabItems[0],
          { value: 'source', label: 'Source' },
          ...tabItems.slice(1),
        ]
      : tabItems;

    return (
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
        <Tabs
          value={tabValue}
          onValueChange={(value) => onTabChange(value as WorkflowPanelTab)}
          className='flex-1 flex flex-col gap-0 h-full'
        >
          {/* Persistent header — switches between workflow and node info */}
          {scopedNode ? (
            <div className='flex shrink-0 flex-col gap-2 p-4 pb-2'>
              <div className='flex items-center gap-3'>
                {scopedNode.icon && (
                  <IconTile
                    icon={scopedNode.icon}
                    backgroundColor={scopedNode.iconColor || ''}
                    iconColor={scopedNode.iconColor ? '#ffffff' : ''}
                    size='small'
                    style={
                      !scopedNode.useThemeColors && scopedNode.iconColor
                        ? {
                            background: `linear-gradient(to bottom, ${scopedNode.iconColor}, color-mix(in srgb, ${scopedNode.iconColor} 85%, black) )`,
                          }
                        : undefined
                    }
                    className={
                      scopedNode.useThemeColors
                        ? 'bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
                        : ''
                    }
                    iconClassName={
                      scopedNode.useThemeColors
                        ? 'size-5 text-white dark:text-slate-950'
                        : 'size-5'
                    }
                  />
                )}
                <TruncateTextWithTooltip
                  text={scopedNode.name || 'Untitled Node'}
                  className='min-w-0 flex-1 text-base font-medium leading-tight text-foreground'
                />
                {isNodeUpdatable(scopedNode) && (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => handleUpdateNode(scopedNode.nodeId)}
                    className='shrink-0 h-8 gap-1.5 text-emerald-700 hover:bg-emerald-700 hover:text-white dark:text-emerald-300 dark:hover:bg-emerald-800/55 dark:hover:text-white'
                  >
                    <CircleFadingArrowUp className='size-4' />
                    Update
                  </Button>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={onCardClose}
                      className='size-9 shrink-0'
                      aria-label={
                        scopedNode
                          ? `Close ${scopedNode.name || 'node'} panel`
                          : 'Close right panel'
                      }
                    >
                      <X className='size-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='left'>
                    <div className='text-center'>Close</div>
                  </TooltipContent>
                </Tooltip>
              </div>
              {scopedNode.description && (
                <div className='text-sm leading-normal text-muted-foreground'>
                  {scopedNode.description}
                </div>
              )}
            </div>
          ) : currentWorkflow ? (
            <div className='flex shrink-0 flex-col gap-2 p-4 pb-2'>
              <div className='flex items-center gap-3'>
                <IconTile
                  icon='WorkflowIcon'
                  backgroundColor=''
                  iconColor=''
                  size='small'
                  className='bg-linear-to-b from-slate-700 to-slate-900 dark:from-white dark:to-slate-200'
                  iconClassName='size-5 text-white dark:text-slate-950'
                />
                <TruncateTextWithTooltip
                  text={currentWorkflow.name || 'Untitled Workflow'}
                  className='min-w-0 flex-1 text-base font-medium leading-tight text-foreground'
                />
                {onEditWorkflowInfo && (
                  <Button
                    variant='ghost'
                    size='icon'
                    className='shrink-0 size-9'
                    onClick={onEditWorkflowInfo}
                  >
                    <PencilLine className='size-4' />
                  </Button>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={onCardClose}
                      className='size-9 shrink-0'
                      aria-label='Close right panel'
                    >
                      <X className='size-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='left'>
                    <div className='text-center'>Close</div>
                  </TooltipContent>
                </Tooltip>
              </div>
              {currentWorkflow.description && (
                <div className='text-sm leading-normal text-muted-foreground'>
                  {currentWorkflow.description}
                </div>
              )}
            </div>
          ) : null}

          <OverflowTabsList
            items={cardTabItems}
            value={tabValue}
            onValueChange={(next) => onTabChange(next as WorkflowPanelTab)}
            triggerClassName={tabTriggerClasses}
          />
          <Separator />

          {scopedNode && (
            <>
              <div className='shrink-0 p-1.5'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => handleShowWorkflowCard(tabValue)}
                  className='gap-2'
                >
                  <CornerLeftUp className='size-4' />
                  <span>{WORKFLOW_CARD_LINK_LABELS[tabValue]}</span>
                </Button>
              </div>
              <Separator />
            </>
          )}

          {/* Events Tab */}
          <TabsContent
            value='events'
            className='flex-1 flex flex-col mt-0 min-h-0'
          >
            <EventsTabContent
              nodeStatuses={nodeStatuses}
              nodeId={scopedNode?.nodeId ?? null}
              onFocusNode={handleNodeSelect}
              isExecuting={isWorkflowExecuting}
              isPaused={isWorkflowPaused}
              isConnected={isConnected}
            />
          </TabsContent>

          {/* Configure Tab */}
          <TabsContent
            value='configure'
            className='flex-1 flex flex-col mt-0 min-h-0'
          >
            {!scopedNode ? (
              <div className='flex flex-col h-full overflow-y-auto'>
                {/* Labels */}
                {isLabelsEnabled && currentWorkflow?.id && (
                  <div className='p-4 border-b'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <span className='text-sm font-medium'>Labels</span>
                      <LabelPicker
                        className='contents'
                        workflowId={currentWorkflow.id}
                        initialLabelIds={currentWorkflowLabels.map((l) => l.id)}
                        orgId={currentOrg?.orgid}
                        customTrigger={
                          <Button variant='ghost' size='sm'>
                            Add Label
                            <Plus className='size-4' />
                          </Button>
                        }
                        renderBadges={(labels, onRemove) =>
                          labels.length > 0 ? (
                            <div className='w-full'>
                              <LabelBadges
                                labels={labels}
                                onRemove={onRemove}
                              />
                            </div>
                          ) : null
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Schedule */}
                {showScheduleTab && workflowId && (
                  <ScheduleConfigSection
                    workflowId={workflowId}
                    workflowName={currentWorkflow?.name}
                    isVisible={activeTab === 'configure'}
                  />
                )}

                {/* Triggers */}
                {showTriggersSection && workflowId && (
                  <TriggersConfigSection
                    workflowId={workflowId}
                    workflowName={currentWorkflow?.name}
                    isVisible={activeTab === 'configure'}
                  />
                )}

                {/* Node List */}
                <WorkflowNodeList
                  nodes={workflowNodes}
                  onNodeSelect={handleNodeSelect}
                />
              </div>
            ) : (
              // Show configurable parameters for each selected node
              <div className='text-sm text-foreground flex-1 overflow-y-auto'>
                {Object.entries(nodeIdToNodeMap)
                  .filter(([nodeId]) => nodeId === scopedNode.nodeId)
                  .map(([nodeId, node]) => {
                    const struct = node.data.struct;
                    const isInputNode =
                      struct.node_sub_type === 'file_input' ||
                      struct.node_sub_type === 'text_input';

                    // Helper function to check if a value is not empty
                    // const isValueNotEmpty = (
                    //   value: NodeParam['value'],
                    // ): boolean => {
                    //   if (value === undefined || value === null) {
                    //     return false;
                    //   }
                    //   if (typeof value === 'string') {
                    //     return value.trim() !== '';
                    //   }
                    //   if (Array.isArray(value)) {
                    //     return value.length > 0;
                    //   }
                    //   // For numbers and booleans, any value is considered non-empty
                    //   // (0 and false are valid values)
                    //   return true;
                    // };

                    const configurableParams = struct.params.filter(
                      (field: NodeParam) => {
                        // Exclude advanced and hidden fields
                        if (field.advanced || field.hidden || !field.show) {
                          return false;
                        }
                        // For bindable fields: only include if value is not empty
                        // This supports legacy workflows that have bindable fields with values
                        // if (field.bindable_config) {
                        //   return isValueNotEmpty(field.value);
                        // }
                        return true;
                      },
                    );
                    const advancedParams = struct.params.filter(
                      (field: NodeParam) =>
                        field.advanced &&
                        !field.hidden &&
                        // Owned by the dedicated Launches section below.
                        field.field_id !== DELEGABLE_WORKFLOWS_FIELD_ID,
                    );
                    const hasNoParams =
                      configurableParams.length === 0 &&
                      advancedParams.length === 0;
                    // --- repeat loop (Phase 3): the Repeat Until node is
                    // configured as one sentence; nodes inside a loop and the
                    // Union / Reduce nodes get an explanatory card first. ---
                    const isRepeatCarrier = struct.node_id === REPEAT_NODE_ID;
                    // --- Phase 6 ---
                    const isMapCarrier = struct.node_id === MAP_NODE_ID;
                    // --- end Phase 6 ---
                    const isCollectOrFold =
                      struct.node_id === UNION_NODE_ID ||
                      struct.node_id === REDUCE_NODE_ID;
                    // --- end repeat loop ---------------------------------------

                    return (
                      <div key={nodeId}>
                        {/* NODE BODY — equal vertical padding keeps the spacing
                        around each node header/divider even. */}
                        <div className='text-sm text-foreground px-1 py-4'>
                          {/* --- explain every decision (Phase 4): what this node
                              decided on the last run, and why, in words. --- */}
                          <DecisionWhyCard nodeId={nodeId} struct={struct} />
                          {/* --- end explain --- */}
                          {/* Custom Code: the pinned script's node.json as a card,
                              before the raw params (script path, SHA, timeout). */}
                          {struct.node_id === CUSTOM_CODE_NODE_ID && (
                            <CustomCodeScriptCard
                              orgId={currentOrg?.orgid}
                              workflowId={workflowId ?? undefined}
                              nodeId={nodeId}
                              struct={struct}
                              onOpenSource={
                                showSourceTab
                                  ? () => onTabChange('source')
                                  : undefined
                              }
                            />
                          )}
                          {/* --- Phase 6: Map / For Each as a form --- */}
                          {isMapCarrier && (
                            <MapPanel
                              nodeId={nodeId}
                              disabled={isLocked || isWorkflowLocked}
                              isExecuting={isWorkflowExecuting}
                            />
                          )}
                          {/* --- end Phase 6 --- */}
                          {/* --- repeat loop (Phase 3) --- */}
                          {isRepeatCarrier ? (
                            <RepeatUntilPanel
                              nodeId={nodeId}
                              disabled={isLocked || isWorkflowLocked}
                            />
                          ) : (
                            <>
                              <RegionNodeRunCard nodeId={nodeId} />
                              {/* --- for each (Phase 8c) --- */}
                              <ForEachRegionCard nodeId={nodeId} />
                              {isCollectOrFold && (
                                <CollectFoldCard nodeId={nodeId} />
                              )}
                            </>
                          )}
                          {/* --- end repeat loop --- */}
                          {isRepeatCarrier ||
                          isMapCarrier ? null : hasNoParams ? (
                            <div className='flex items-center justify-center h-full text-sm text-muted-foreground'>
                              No configurable parameters
                            </div>
                          ) : (
                            <>
                              {/* Regular configurable params */}
                              {configurableParams.map(
                                (field: NodeParam, index: number) => {
                                  const isHighlighted =
                                    highlightedNodeId === nodeId &&
                                    highlightedFieldId === field.field_id;
                                  return (
                                    <React.Fragment
                                      key={`${nodeId}-${field.field_id}-${index}`}
                                    >
                                      <NodeInputRenderer
                                        field={field}
                                        handleParameterChange={(
                                          fieldId,
                                          value,
                                          key,
                                        ) =>
                                          storeHandleParameterChange(
                                            nodeId,
                                            fieldId,
                                            value,
                                            key,
                                          )
                                        }
                                        nodeType={struct.node_type}
                                        nodeId={struct.react_flow_generated_id}
                                        workflowId={currentWorkflow?.id}
                                        isHighlighted={isHighlighted}
                                        onHighlightComplete={() =>
                                          setHighlightedField(null, null)
                                        }
                                        isDisabled={
                                          isLocked ||
                                          (isWorkflowLocked &&
                                            !isInputNode &&
                                            !WORKFLOW_LOCK_EDITABLE_FIELDS.has(
                                              field.field_id,
                                            ))
                                        }
                                      />
                                    </React.Fragment>
                                  );
                                },
                              )}

                              {/* NOTE: TEMPORARILY HIDING Advanced Settings section for now */}
                              {SHOW_ADVANCED_SETTINGS &&
                                advancedParams.length > 0 && (
                                  <Collapsible
                                    open={advancedSettingsOpen[nodeId] ?? false}
                                    onOpenChange={(open) =>
                                      setAdvancedSettingsOpen((prev) => ({
                                        ...prev,
                                        [nodeId]: open,
                                      }))
                                    }
                                    className='mt-4 rounded-lg'
                                  >
                                    <CollapsibleTrigger asChild>
                                      <Button
                                        variant='ghost'
                                        className='w-full flex items-center justify-between px-3 py-2 h-auto hover:bg-muted/50'
                                      >
                                        <div className='flex items-center gap-2'>
                                          <Settings2 className='h-4 w-4 text-muted-foreground' />
                                          <span className='text-sm font-medium'>
                                            Advanced Settings
                                          </span>
                                          <span className='text-xs text-muted-foreground'>
                                            ({advancedParams.length})
                                          </span>
                                        </div>
                                        <ChevronDown
                                          className={cn(
                                            'h-4 w-4 text-muted-foreground transition-transform duration-200',
                                            advancedSettingsOpen[nodeId] &&
                                              'rotate-180',
                                          )}
                                        />
                                      </Button>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className='px-1 pb-3'>
                                      <Separator className='mb-3' />
                                      {advancedParams.map(
                                        (field: NodeParam, index: number) => {
                                          const isHighlighted =
                                            highlightedNodeId === nodeId &&
                                            highlightedFieldId ===
                                              field.field_id;
                                          return (
                                            <NodeInputRenderer
                                              key={`${nodeId}-advanced-${field.field_id}-${index}`}
                                              field={{
                                                ...field,
                                                show: true,
                                              }}
                                              handleParameterChange={(
                                                fieldId,
                                                value,
                                                key,
                                              ) =>
                                                storeHandleParameterChange(
                                                  nodeId,
                                                  fieldId,
                                                  value,
                                                  key,
                                                )
                                              }
                                              nodeType={struct.node_type}
                                              nodeId={
                                                struct.react_flow_generated_id
                                              }
                                              workflowId={currentWorkflow?.id}
                                              isHighlighted={isHighlighted}
                                              onHighlightComplete={() =>
                                                setHighlightedField(null, null)
                                              }
                                              isDisabled={
                                                isLocked ||
                                                (isWorkflowLocked &&
                                                  !isInputNode &&
                                                  !WORKFLOW_LOCK_EDITABLE_FIELDS.has(
                                                    field.field_id,
                                                  ))
                                              }
                                            />
                                          );
                                        },
                                      )}
                                    </CollapsibleContent>
                                  </Collapsible>
                                )}
                            </>
                          )}
                        </div>
                        {/* Launches: scoped workflow-launch targets for agent
                          nodes. Renders null for other nodes. */}
                        <NodeLaunchesSection
                          struct={struct}
                          currentWorkflowId={currentWorkflow?.id}
                          isDisabled={
                            isLocked || (isWorkflowLocked && !isInputNode)
                          }
                          onValueChange={(value) =>
                            storeHandleParameterChange(
                              nodeId,
                              DELEGABLE_WORKFLOWS_FIELD_ID,
                              value,
                            )
                          }
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </TabsContent>

          {/* Source Tab — provenance + read-only/editable source for pinned
            deterministic custom-code nodes */}
          {showSourceTab && scopedNode && currentOrg?.orgid && workflowId && (
            <TabsContent
              value='source'
              className='flex-1 flex flex-col mt-0 min-h-0 overflow-hidden'
            >
              <SourceCodeWorkspace
                orgId={currentOrg.orgid}
                workflowId={workflowId}
                runId={workflowRunRecordId ?? undefined}
                nodeId={scopedNode.nodeId}
              />
            </TabsContent>
          )}

          {/* MCP Tab — real gateway endpoint + connection snippet when the
            publish response carried one, legacy sample snippet otherwise */}
          {mcpCode &&
            (mcpEndpoint ? (
              <McpPublishTabContent
                mcpCode={mcpCode}
                mcpEndpoint={mcpEndpoint}
              />
            ) : (
              <CodeTabContent value='mcp' code={mcpCode} language='python' />
            ))}

          {/* Files Tab */}
          <TabsContent
            value='files'
            className='flex-1 flex flex-col mt-0 min-h-0'
          >
            <FilesTabContent
              nodes={workflowNodes}
              selectedNodeId={scopedNode?.nodeId ?? null}
              workflowName={currentWorkflow?.name}
            />
          </TabsContent>

          {/* Evals Tab */}
          <TabsContent
            value='evals'
            className='flex-1 flex flex-col mt-0 min-h-0 min-w-0 overflow-hidden'
          >
            <EvalsTabContent
              workflowId={workflowId}
              workflowRunId={workflowRunId}
              parentRunId={parentRunId}
              cachedEvalNodeIds={cachedEvalNodeIds}
              isWorkflowExecuting={isWorkflowExecuting}
              evalNodes={allEvalNodes}
              autoSelectedEvalNodeId={autoSelectedEvalNodeId}
            />
          </TabsContent>

          {/* Errors Tab */}
          {showErrorsNotification && (
            <TabsContent
              value='errors'
              className='flex-1 flex flex-col mt-0 min-h-0'
            >
              <ErrorsTabContent />
            </TabsContent>
          )}
        </Tabs>
      </div>
    );
  };

  const selectedNodeCards = Object.entries(nodeIdToNodeMap).map(
    ([nodeId, node]) => describeNode(nodeId, node),
  );

  // One card per selected node, each speaking for its own node; the workflow's
  // own card stands in when nothing is selected.
  const cards =
    selectedNodeCards.length > 0
      ? selectedNodeCards.map((scoped) => {
          const resolvedTab = resolveCardTab(scoped.nodeId);

          return (
            <div
              key={scoped.nodeId}
              className={cn(
                rightPanelCardClasses,
                // A lone card fills the slot; a stack sizes to its contents so
                // every selected node is reachable, each card capped to the slot.
                selectedNodeCards.length === 1
                  ? 'min-h-0 flex-1'
                  : 'max-h-full shrink-0',
              )}
            >
              {renderPanelBody(
                scoped,
                resolvedTab,
                (tab) => handleCardTabChange(scoped.nodeId, tab),
                () => handleCloseNodeCard(scoped.nodeId),
              )}
            </div>
          );
        })
      : [
          <div
            key='workflow'
            className={cn(rightPanelCardClasses, 'min-h-0 flex-1')}
          >
            {renderPanelBody(
              null,
              activeTab,
              (tab) => setActiveTab(tab),
              handleClosePanel,
            )}
          </div>,
        ];

  // Floating panel state (intermediate) - when node is focused but not maximized
  if (showFloatingPanel) {
    return (
      <>
        <div className='absolute top-2 right-2 z-50 flex items-center gap-2'>
          {headerButtons}
        </div>
        <RightPanelSlot width={panelWidth}>
          {/* Vertical Resize Line */}
          <div
            className='absolute left-0 top-2 bottom-2 z-10 flex w-px cursor-ew-resize items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2'
            onMouseDown={handleResizeStart}
          >
            <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border'>
              <GripVerticalIcon className='size-2.5' />
            </div>
          </div>
          <div className='hidden-scrollbar flex h-full flex-col gap-2 overflow-x-clip overflow-y-auto'>
            {cards}
          </div>
        </RightPanelSlot>
      </>
    );
  }

  // Maximized/Pinned state - full panel attached to edge with custom resize
  return (
    <div
      className='absolute top-0 right-0 bottom-0 z-40'
      style={{ width: panelWidth }}
    >
      {/* Resize handle on left edge */}
      <div
        className='absolute left-0 top-0 bottom-0 w-px cursor-ew-resize bg-border z-10 flex items-center justify-center after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2'
        onMouseDown={handleResizeStart}
      >
        <div className='bg-border z-10 flex h-4 w-3 items-center justify-center rounded-xs border'>
          <GripVerticalIcon className='size-2.5' />
        </div>
      </div>
      <div className='h-full animate-in zoom-in-95 origin-top-right duration-200'>
        <div className='h-full bg-background border-l shadow-lg flex flex-col overflow-hidden'>
          {/* Header */}
          <div className='p-2 shrink-0'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                {/* Close button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      onClick={handleClosePanel}
                      className='h-8 w-8'
                      aria-label='Close right panel'
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side='left'>
                    <div className='text-center'>Close</div>
                  </TooltipContent>
                </Tooltip>

                {!isWorkflowExecuting && (
                  <Separator orientation='vertical' className='h-5' />
                )}
              </div>
              {headerButtons}
            </div>
          </div>

          <div className='hidden-scrollbar flex flex-1 flex-col gap-2 overflow-x-clip overflow-y-auto p-2'>
            {cards}
          </div>
        </div>
      </div>
    </div>
  );
};
